import React, { useState } from 'react';
import { Typography, Button, Modal, TextField, TextArea } from '@scale/scaleui';
import { AuditPermissionRole } from '@scale/shared/taskerScreening/types';
import SelectCourse from 'react-scale/corp/components/SelectCourse';
import SelectCourseSections from 'react-scale/corp/components/SelectCourseSections';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { WorkerSkillableCourseSections } from '@scale/shared/types/params/course';

interface TaskerScreeningAuditPermissionRoleCreationModalProps {
  role: AuditPermissionRole;
  setRole: (role: AuditPermissionRole) => void;
  open: boolean;
  onClose: () => void;
  onPublish: (isArchive?: boolean) => void;
  buttonText?: string;
  canArchive?: boolean;
}

const TaskerScreeningAuditPermissionRoleCreationContents: React.FC<
  TaskerScreeningAuditPermissionRoleCreationModalProps
> = ({ role, setRole, onPublish, buttonText, canArchive }) => {
  const [courseSelect, setCourseSelect] = useState<{ label: string; value: string } | undefined>(
    role.courseIds?.length ? { label: role.courseIds[0], value: role.courseIds[0] } : undefined,
  );
  const [sectionTitleSelects, setSectionTitleSelects] = useState<
    { label: string; value: string }[]
  >(role.sectionTitles?.length ? role.sectionTitles.map(t => ({ label: t, value: t })) : []);
  const disableCreate = !role.name?.length || !role.courseIds.length || !role.sectionTitles.length;
  return (
    <div className="scale-ui scale-theme scale-tailwind w-[600px]">
      <Typography.Paragraph2>Role Name</Typography.Paragraph2>
      <TextField
        className="mt-2 mb-4"
        id="name"
        type="text"
        value={role.name}
        placeholder="Role Name"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setRole({
            ...role,
            name: e.target.value,
          });
        }}
      />
      <div className="mb-2">
        <Typography.Paragraph2>Select Course</Typography.Paragraph2>
      </div>
      <SelectCourse
        value={courseSelect}
        onChange={(course: { label: string; value: string }) => {
          setCourseSelect(course);
          if (course?.value) {
            setRole({
              ...role,
              courseIds: [course.value],
            });
          }
        }}
      />
      <div className="mt-4 mb-2">
        <Typography.Paragraph2>Select Section Titles</Typography.Paragraph2>
      </div>
      <SelectCourseSections
        key={courseSelect?.value ?? 'sections'}
        courseId={courseSelect?.value ?? ''}
        value={sectionTitleSelects}
        onChange={(titles: { value: string; label: string }[]) => {
          setSectionTitleSelects(titles);
          setRole({
            ...role,
            sectionTitles: titles.map(t => t.value),
          });
        }}
        courseSectionTypes={WorkerSkillableCourseSections}
      />
      {courseSelect?.value && (
        <div className="mt-4 mb-4 w-full">
          <SmartLink type={INamedObjectTypes.Course} id={courseSelect.value} />
        </div>
      )}
      <div className="mt-4 mb-2">
        <Typography.Paragraph2>Role Description</Typography.Paragraph2>
      </div>
      <TextArea
        className="mb-4"
        id="description"
        value={role.description}
        placeholder="Role Name"
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          setRole({
            ...role,
            description: e.target.value,
          });
        }}
      />
      <div className="flex gap-2">
        <Button
          className="mt-4"
          variant="primary"
          onClick={() => {
            onPublish();
          }}
          disabled={disableCreate}
        >
          {buttonText ?? 'Publish Audit Permission Role'}
        </Button>
        <div className="flex-grow"></div>
        {canArchive && (
          <Button
            className="mt-4"
            variant="danger"
            onClick={() => {
              onPublish(true);
            }}
          >
            Archive
          </Button>
        )}
      </div>
    </div>
  );
};

const TaskerScreeningAuditPermissionRoleCreationModal: React.FC<
  TaskerScreeningAuditPermissionRoleCreationModalProps
> = ({ open, onClose, role, setRole, onPublish, buttonText, canArchive }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      theme="scale-theme"
      title="Audit Permission Role Creation"
      innerClassName="overflow-y-auto"
    >
      <TaskerScreeningAuditPermissionRoleCreationContents
        open={open}
        onClose={onClose}
        role={role}
        setRole={setRole}
        onPublish={onPublish}
        buttonText={buttonText}
        canArchive={canArchive}
      />
    </Modal>
  );
};

export default TaskerScreeningAuditPermissionRoleCreationModal;
